import { Button, useClickOutside, useDropdownDirection } from '@consigli/facade';
import {
  type MeetingAgreement,
  type ProjectParticipant,
  type ResponsibleParty,
} from '@consigli/types';
import { FC, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPencilAlt, FaQuoteRight, FaTrash } from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';

import { ConfirmDialog } from './confirm-dialog';
import { DeleteConfirmModal } from './delete-confirm-modal';
import { InlineEditableText } from './inline-editable-text';
import { ProvisionalBadge } from './provisional-badge';
import { ReassignPersonPopover, type ReassignResultItem } from './reassign-person-popover';
import { Avatar } from '../utils/participant-utils';

interface AgreementCardProps {
  agreement: MeetingAgreement;
  participants: ProjectParticipant[];
  customParties?: ResponsibleParty[];
  /** Persist the full desired set of responsible parties; resolves to success. */
  onSaveParties: (agreementId: string, result: ReassignResultItem[]) => Promise<boolean>;
  /** Persist new agreement text; resolves to success. */
  onSaveText?: (agreementId: string, text: string) => Promise<boolean>;
  /** Delete the agreement. */
  onDelete?: (agreementId: string) => void;
  isDeleting?: boolean;
  /** Open the transcript viewer scrolled to this agreement's source segments. */
  onShowSource?: (agreement: MeetingAgreement) => void;
  /**
   * Ask before a change that the organizer's re-run with audio would throw away: for whoever
   * is not the organizer, on an agreement that is still provisional.
   */
  warnBeforeChange?: boolean;
}

type PendingChange = 'text' | 'parties';

export const AgreementCard: FC<AgreementCardProps> = ({
  agreement,
  participants,
  customParties,
  onSaveParties,
  onSaveText,
  onDelete,
  isDeleting = false,
  onShowSource,
  warnBeforeChange = false,
}) => {
  const { t } = useTranslation();
  const parties = agreement.responsibleParties ?? [];

  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null);
  // The change the user confirmed, started once the dialog has closed and returned focus.
  const confirmedChangeRef = useRef<PendingChange | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const commitEdit = useCallback(
    (text: string) => onSaveText?.(agreement.id, text),
    [agreement.id, onSaveText],
  );

  const openDirection = useDropdownDirection({ containerRef, isOpen, dropdownHeight: 400 });

  const closePopover = useCallback(() => {
    if (isSaving) return;
    setIsOpen(false);
  }, [isSaving]);

  useClickOutside({ ref: containerRef, onClickOutside: closePopover, enabled: isOpen });

  const handleSave = useCallback(
    async (result: ReassignResultItem[]) => {
      setIsSaving(true);
      try {
        // Keep the popover open on failure so the user can retry.
        if (await onSaveParties(agreement.id, result)) {
          setIsOpen(false);
        }
      } finally {
        setIsSaving(false);
      }
    },
    [agreement.id, onSaveParties],
  );

  const startChange = useCallback((change: PendingChange) => {
    if (change === 'text') setIsEditing(true);
    else setIsOpen(true);
  }, []);

  const requestChange = useCallback(
    (change: PendingChange) => {
      if (warnBeforeChange) setPendingChange(change);
      else startChange(change);
    },
    [warnBeforeChange, startChange],
  );

  const canShowSource = (agreement.sourceSegmentIds?.length ?? 0) > 0 && !!onShowSource;

  const showActions = canShowSource || !!onSaveText || !!onDelete;

  return (
    <div
      className={`group/card p-4 border rounded-lg ${
        agreement.active ? 'bg-gray-50 border-gray-200' : 'bg-gray-50 border-gray-300 opacity-60'
      }`}
    >
      <div className="flex items-start space-x-3">
        <div
          className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
            agreement.active ? 'bg-gray-500' : 'bg-gray-400'
          }`}
        />
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <InlineEditableText
              initialValue={agreement.agreement ?? ''}
              onCommit={commitEdit}
              onCancel={() => setIsEditing(false)}
            />
          ) : (
            <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap">
              {agreement.agreement}
            </p>
          )}

          <div className="group relative mt-1.5 flex items-center gap-1.5" ref={containerRef}>
            <span className="text-xs text-meeting-600">
              {t('meeting-secretary.meetings.responsible-label')}:
            </span>
            {parties.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1">
                {parties.map((p, index) => (
                  <span
                    key={p.meetingUserId ?? p.customPartyId ?? p.name ?? index}
                    className="inline-flex items-center gap-1 rounded-full bg-brand-light/50 py-0.5 pl-0.5 pr-2 text-xs text-gray-800"
                  >
                    <Avatar name={p.name} className="!h-5 !w-5 !text-[10px] !ring-0" />
                    <span className="max-w-[10rem] truncate">{p.name}</span>
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-xs text-gray-400">-</span>
            )}

            <Button
              tertiary
              iconOnly
              icon={FiEdit2}
              iconSize={13}
              onClick={(e) => {
                e.stopPropagation();
                if (isOpen) setIsOpen(false);
                else requestChange('parties');
              }}
              className={`!p-1 text-gray-400 hover:text-brand transition-all focus:opacity-100 group-hover:opacity-100 ${
                isOpen ? 'opacity-100' : 'opacity-0'
              }`}
              title={t('meeting-secretary.meetings.edit-responsible-parties')}
              aria-label={t('meeting-secretary.meetings.edit-responsible-parties')}
            />

            {isOpen && (
              <ReassignPersonPopover
                title={t('meeting-secretary.meetings.edit-responsible-parties')}
                participants={participants}
                mode="multi"
                allowCustom
                customParties={customParties}
                selected={parties.map((p) => ({
                  meetingUserId: p.meetingUserId,
                  customPartyId: p.customPartyId,
                  name: p.name,
                }))}
                isSaving={isSaving}
                direction={openDirection}
                onSave={handleSave}
                onClose={closePopover}
              />
            )}
          </div>

          <div className="flex items-center gap-2 mt-2">
            {agreement.provisional && <ProvisionalBadge />}
            {!agreement.active && (
              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full font-medium">
                {t('meeting-secretary.meetings.inactive-label')}
              </span>
            )}
            {agreement.hasMeetingContradictions && (
              <span className="text-xs px-2 py-1 bg-meeting-600/10 text-meeting-600 rounded-full">
                {t('meeting-secretary.meetings.meeting-contradiction-label')}
              </span>
            )}
            {agreement.hasDocumentContradictions && (
              <span className="text-xs px-2 py-1 bg-meeting-700 text-white rounded-full">
                {t('meeting-secretary.meetings.document-contradiction-label')}
              </span>
            )}
          </div>
        </div>

        {showActions && (
          <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover/card:opacity-100 focus-within:opacity-100 transition-opacity">
            {canShowSource && (
              <Button
                tertiary
                iconOnly
                icon={FaQuoteRight}
                iconSize={14}
                onClick={(e) => {
                  e.stopPropagation();
                  onShowSource?.(agreement);
                }}
                className="!p-2 rounded-full text-gray-400 hover:text-brand hover:bg-day-light-3 transition-colors"
                title={t('meeting-secretary.meetings.view-transcript-source')}
                aria-label={t('meeting-secretary.meetings.view-transcript-source')}
              />
            )}
            {onSaveText && (
              <Button
                tertiary
                iconOnly
                icon={FaPencilAlt}
                iconSize={14}
                onClick={(e) => {
                  e.stopPropagation();
                  requestChange('text');
                }}
                className="!p-2 rounded-full text-gray-400 hover:text-brand hover:bg-day-light-3 transition-colors"
                title={t('meeting-secretary.meetings.edit-agreement')}
                aria-label={t('meeting-secretary.meetings.edit-agreement')}
              />
            )}
            {onDelete && (
              <Button
                tertiary
                iconOnly
                icon={FaTrash}
                iconSize={14}
                disabled={isDeleting}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDeleteModalOpen(true);
                }}
                className="!p-2 rounded-full text-gray-400 hover:text-error-dark hover:bg-day-light-3 transition-colors"
                title={t('meeting-secretary.meetings.delete-agreement')}
                aria-label={t('meeting-secretary.meetings.delete-agreement')}
              />
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={pendingChange !== null}
        onClose={() => setPendingChange(null)}
        onConfirm={() => {
          confirmedChangeRef.current = pendingChange;
          setPendingChange(null);
        }}
        onClosed={() => {
          const change = confirmedChangeRef.current;
          confirmedChangeRef.current = null;
          if (change) startChange(change);
        }}
        title={t('meeting-secretary.meetings.provisional-warning-title')}
        confirmLabel={t('meeting-secretary.meetings.provisional-warning-confirm')}
      >
        {t('meeting-secretary.meetings.provisional-warning-message')}
      </ConfirmDialog>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() => {
          setIsDeleteModalOpen(false);
          onDelete?.(agreement.id);
        }}
        titleKey="meeting-secretary.meetings.delete-agreement"
        messageKey="meeting-secretary.meetings.confirm-delete-agreement"
      />
    </div>
  );
};
